import {
  BOARD,
  DEFAULT_TIME_RANGE,
  longerTimeRange,
  maxTilesForArea,
  RADIUS_OPTIONS,
  radiusOption,
  TIME_RANGES,
  timeRange,
  widerRadius,
  type BoardPost,
  type RadiusId,
  type TimeRangeId,
} from '@talkofthetown/shared';
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Board } from './components/Board';
import { Footer } from './components/Footer';
import { Header } from './components/Header';
import { InfoDialog, type InfoSection } from './components/InfoDialog';
import { openPlaceMenu } from './components/PlaceMenu';
import { MyPostsDialog } from './components/MyPostsDialog';
import { PostDialog } from './components/PostDialog';
import { ReviewDialog } from './components/ReviewDialog';
import { StatsDialog } from './components/StatsDialog';
import { useToast } from './components/Toasts';
import { config } from './config';
import { useElementSize, useNow } from './hooks/misc';
import { useAuth } from './hooks/useAuth';
import { useBoard } from './hooks/useBoard';
import { useFrozenBoard } from './hooks/useFrozenBoard';
import { usePromote } from './hooks/usePromote';
import { useViewerLocation } from './hooks/useViewerLocation';
import { friendlyError } from './lib/errors';
import { townAt } from './lib/places';

const ComposeDialog = lazy(() => import('./components/ComposeDialog'));

const RADIUS_KEY = 'tott.radius';
const RANGE_KEY = 'tott.range';

/** What was picked last time in this browser (one of `ids`), or the default. */
function saved<T extends string>(key: string, ids: readonly T[], fallback: T): T {
  try {
    const value = localStorage.getItem(key);
    return ids.find((id) => id === value) ?? fallback;
  } catch {
    return fallback;
  }
}

function save(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode: fine, we just won't remember */
  }
}

/** The distance picked last time (10 km at first) and how far back to look (24 hours at first). */
const savedRadius = () =>
  saved<RadiusId>(
    RADIUS_KEY,
    RADIUS_OPTIONS.map((o) => o.id),
    '10km',
  );
const savedRange = () =>
  saved<TimeRangeId>(
    RANGE_KEY,
    TIME_RANGES.map((o) => o.id),
    DEFAULT_TIME_RANGE,
  );

/** Policy pages with their own address (/privacy, /terms), e.g. for Google's consent message. */
function infoFromUrl(): InfoSection | null {
  const page = /^\/(privacy|terms)\/?$/.exec(location.pathname)?.[1];
  return (page as InfoSection | undefined) ?? null;
}

/** A post to open from the URL: /p/<id> (shared links) or ?p=<id> (in-app history). */
function postIdFromUrl(): string | null {
  const fromPath = /^\/p\/([A-Za-z0-9]{1,40})\/?$/.exec(location.pathname)?.[1];
  const id = fromPath ?? new URLSearchParams(location.search).get('p');
  return id && /^[A-Za-z0-9]{1,40}$/.test(id) ? id : null;
}

export function App() {
  const toast = useToast();
  const auth = useAuth();
  const loc = useViewerLocation();
  const now = useNow();

  const [radius, setRadius] = useState<RadiusId>(savedRadius);
  const [range, setRange] = useState<TimeRangeId>(savedRange);
  const [openId, setOpenId] = useState<string | null>(postIdFromUrl);
  // The post in the dialog: it stays while the dialog shrinks back into its tile after closing.
  const [shownId, setShownId] = useState<string | null>(openId);
  if (openId && openId !== shownId) setShownId(openId);
  const [openSummary, setOpenSummary] = useState<BoardPost | null>(null);
  const [composing, setComposing] = useState(false);
  const [info, setInfo] = useState<InfoSection | null>(infoFromUrl);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const [myPostsOpen, setMyPostsOpen] = useState(false);
  const promote = usePromote();

  const board = useBoard(loc.center, radius, now, now - timeRange(range).ms);
  // At most five voices fit the middle (fewer on a small screen); the board stays put until the
  // reader refreshes.
  const [mainRef, mainSize] = useElementSize<HTMLDivElement>();
  const fit = Math.min(BOARD.maxVoices, maxTilesForArea(mainSize.width * mainSize.height));
  const frozen = useFrozenBoard(
    board,
    `${board.radius}|${range}|${loc.center?.lat},${loc.center?.lng}`,
    fit,
  );

  // A town's page (/town/<slug>, e.g. from a search engine): look around that town.
  const choosePlace = loc.choose;
  useEffect(() => {
    const slug = /^\/town\/([a-z0-9-]{3,80})\/?$/.exec(location.pathname)?.[1];
    if (!slug) return;
    void townAt(slug).then((town) => town && choosePlace(town));
  }, [choosePlace]);

  // Deep links: /?p=<id> opens a post; Back closes it.
  useEffect(() => {
    const onPop = () => setOpenId(postIdFromUrl());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const openPost = useCallback((post: BoardPost) => {
    setOpenSummary(post);
    setOpenId(post.id);
    history.pushState({ p: post.id }, '', `?p=${post.id}`);
  }, []);

  /** Opens a post picked from a list (My posts, the review queue), closing that list. */
  const openFromList = (close: () => void) => (id: string) => {
    close();
    setOpenSummary(null);
    setOpenId(id);
    history.pushState({ p: id }, '', `?p=${id}`);
  };

  const closePost = useCallback(() => {
    setOpenId(null);
    if (postIdFromUrl()) {
      if (history.state?.p) history.back();
      else history.replaceState(null, '', '/');
    }
  }, []);

  const signIn = useCallback(() => {
    auth.signInWithGoogle().catch((e) => toast(friendlyError(e)));
  }, [auth, toast]);

  // A distance is measured from a place: picking one asks for the location if there's none yet.
  const changeRadius = (r: RadiusId) => {
    setRadius(r);
    save(RADIUS_KEY, r);
    if (r !== 'world' && !loc.center) loc.request();
  };

  const changeRange = (r: TimeRangeId) => {
    setRange(r);
    save(RANGE_KEY, r);
  };

  const pickPlace = (
    <button type="button" className="linklike" onClick={openPlaceMenu}>
      pick a town
    </button>
  );
  const notice =
    !loc.center && loc.status === 'idle' ? (
      <>
        Showing the whole world.{' '}
        <button type="button" className="linklike" onClick={loc.request}>
          See what’s happening near you
        </button>{' '}
        or {pickPlace}.
      </>
    ) : !loc.center && loc.status === 'denied' && radius !== 'world' ? (
      <>Location is blocked for this site, so showing the whole world. You can {pickPlace}.</>
    ) : null;
  const around = loc.place ? ` of ${loc.place.name}` : '';
  const scope =
    board.radius === 'world'
      ? 'here'
      : `${radiusOption(board.radius).label.toLowerCase()}${around}`;
  const look = (r: RadiusId) => ({
    label: r === 'world' ? 'See the whole world' : `Look ${radiusOption(r).label.toLowerCase()}`,
    onClick: () => changeRadius(r),
  });
  // A quiet board says so and offers a wider distance and a longer time.
  const wider = loc.center ? widerRadius(board.radius) : null;
  const longer = longerTimeRange(range);
  const empty = {
    text: `Nothing ${scope} in the last ${timeRange(range).label}.`,
    actions: [
      ...(wider ? [look(wider)] : []),
      ...(longer
        ? [{ label: `Look back ${timeRange(longer).label}`, onClick: () => changeRange(longer) }]
        : []),
    ],
  };
  const shownRadius: RadiusId = loc.center
    ? board.radius
    : loc.status === 'locating'
      ? radius
      : 'world';

  /** Emulator only: the emulator doesn't run schedules, so trigger the ranking job by hand. */
  async function devCall(fn: 'devRankNow' | 'devAgentsNow') {
    try {
      const res = await fetch(
        `http://${config.emulatorHost}:5001/${config.firebase.projectId}/us-central1/${fn}`,
      );
      const s = (await res.json()) as Record<string, number>;
      toast(
        fn === 'devRankNow'
          ? `Ranked: ${s.opens} opens, ${s.areaWrites} boards updated`
          : `AI agents: ${s.posted} posted, ${s.skipped} skipped`,
      );
    } catch (e) {
      toast(friendlyError(e));
    }
  }

  // Around the voices: new and faded posts, then the voices too quiet for the middle.
  const whispers = useMemo(
    () => [...frozen.whispers, ...frozen.hidden],
    [frozen.whispers, frozen.hidden],
  );

  return (
    <div className="app">
      <Header
        auth={auth}
        radius={shownRadius}
        range={range}
        place={loc.place}
        followingLocation={!loc.place && !!loc.center}
        locationStatus={loc.status}
        onRadiusChange={changeRadius}
        onRangeChange={changeRange}
        onChoosePlace={loc.choose}
        onUseMyLocation={loc.request}
        changed={frozen.changed}
        changedScope={scope}
        onRefresh={frozen.refresh}
        onCompose={() => setComposing(true)}
        onSignIn={signIn}
        onReview={() => setReviewOpen(true)}
        onStats={() => setStatsOpen(true)}
        onMyPosts={() => setMyPostsOpen(true)}
      />

      {(notice || config.useEmulators) && (
        <div className="subbar">
          {notice && <p className="notice">{notice}</p>}
          {config.useEmulators && (
            <span className="dev-only">
              <button
                type="button"
                className="btn btn--small btn--ghost"
                onClick={() => devCall('devRankNow')}
              >
                Dev: run ranking
              </button>{' '}
              <button
                type="button"
                className="btn btn--small btn--ghost"
                onClick={() => devCall('devAgentsNow')}
              >
                Dev: run AI agents
              </button>
            </span>
          )}
        </div>
      )}

      <main className="layout">
        <div className="layout__main" ref={mainRef}>
          <Board
            posts={frozen.posts}
            snapshot={frozen.snapshot}
            stale={board.stale}
            now={now}
            whispers={whispers}
            sponsored={frozen.sponsored}
            whisperNote={`No voices ${scope} yet, only whispers: new posts nobody has opened.`}
            empty={empty}
            onOpen={openPost}
            onCompose={() => setComposing(true)}
          />
        </div>
      </main>

      <Footer onInfo={setInfo} />

      <PostDialog
        key={shownId ?? 'closed'}
        postId={shownId}
        open={openId !== null && openId === shownId}
        summary={openSummary}
        center={loc.center}
        now={now}
        auth={auth}
        onClose={closePost}
        onClosed={() => {
          // Unless another post opened meanwhile.
          setShownId((id) => (id === shownId ? null : id));
          setOpenSummary((s) => (s?.id === shownId ? null : s));
        }}
      />
      {composing && (
        <Suspense fallback={null}>
          <ComposeDialog
            open={composing}
            onClose={() => setComposing(false)}
            auth={auth}
            center={loc.center}
            placeName={loc.place?.name ?? null}
            locationStatus={loc.status}
            onRequestLocation={loc.request}
            onPosted={(id, paid) => {
              setComposing(false);
              if (paid) void promote(id, paid.plan, paid.currency);
              else
                toast(
                  'Posted! It’s a whisper for now: it gets a voice on the board as people open it.',
                );
            }}
          />
        </Suspense>
      )}
      <InfoDialog
        open={info !== null}
        section={info ?? 'about'}
        onClose={() => {
          setInfo(null);
          // Opened at /privacy or /terms: closing leaves the board at its own address.
          if (infoFromUrl()) history.replaceState(null, '', '/');
        }}
      />
      {auth.signedIn && auth.user && (
        <MyPostsDialog
          open={myPostsOpen}
          uid={auth.user.uid}
          now={now}
          onClose={() => setMyPostsOpen(false)}
          onOpenPost={openFromList(() => setMyPostsOpen(false))}
        />
      )}
      {auth.admin && <StatsDialog open={statsOpen} onClose={() => setStatsOpen(false)} />}
      {auth.admin && (
        <ReviewDialog
          open={reviewOpen}
          now={now}
          onClose={() => setReviewOpen(false)}
          onOpenPost={openFromList(() => setReviewOpen(false))}
        />
      )}
    </div>
  );
}
